import type { SolarProposalDesignReadDto } from "~/api/dto/solar-proposal-design.dto"
import type { SolarProposalPricingDto } from "~/api/dto/solar-proposal-pricing.dto"
import type { SolarProposalSavingsSectionDto } from "~/api/dto/solar-proposal-savings.dto"
import {
  isSolarLightReachPricingRequiredError,
  SAVINGS_SUPERSEDED_CODE,
  SAVINGS_SUPERSEDED_MESSAGE,
  SOLAR_SAVINGS_NO_ACTIVE_DESIGN_CODE,
  SOLAR_SAVINGS_NOT_READY_CODE,
  solarProposalErrorCode,
} from "~/api/solar-proposal-errors"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  GET_LIGHTREACH_PRICING_LABEL,
  LIGHTREACH_PRICING_OUT_OF_DATE,
  LIGHTREACH_UPDATE_STALE_LABEL,
  OPEN_LIGHTREACH_LOAN_PRODUCT_LABEL,
} from "./solar-lightreach-copy"
import { solarPricingErrorMessage } from "./solar-pricing-error"
import { solarSetupStepPath } from "./solar-setup-paths"
import type {
  UpdateCopy,
  UpdateFix,
  UpdatesSpec,
} from "../ProposalPresentation/updates/proposal-updates"

export const RECALCULATE_SAVINGS_LABEL = `Recalculate savings`
export const RECALCULATE_SAVINGS_BUSY_LABEL = `Recalculating`
export const RECALCULATE_SAVINGS_ERROR_PROMPT = `Unable to recalculate savings`
export const PRODUCTION_RETRY_ERROR_PROMPT = `Production couldn't be updated. Try again.`

/** A recalculation that fails keeps the figures on screen; this explains why. */
export const describeRecalculateError = (error: unknown): string => {
  switch (solarProposalErrorCode(error)) {
    case SAVINGS_SUPERSEDED_CODE:
      return SAVINGS_SUPERSEDED_MESSAGE
    case SOLAR_SAVINGS_NO_ACTIVE_DESIGN_CODE:
    case SOLAR_SAVINGS_NOT_READY_CODE:
      return parseAxiosError(error, RECALCULATE_SAVINGS_ERROR_PROMPT)
    default:
      return RECALCULATE_SAVINGS_ERROR_PROMPT
  }
}

/**
 * What can be out of date on a Solar Proposal, in the order a fix runs: savings
 * read the price (a cash system's net savings subtract it), and both read the
 * production.
 */
export const SOLAR_UPDATE_ORDER = [`production`, `pricing`, `savings`] as const
export type SolarUpdateKey = (typeof SOLAR_UPDATE_ORDER)[number]

/**
 * What each update reads, in fix order: the PowurOS pricing request carries
 * the design's estimated production, and savings are built on the production
 * and (for a cash system's net savings) the price. A fix that would build on
 * stale production runs production first, and stops if it fails.
 */
export const SOLAR_UPDATE_DEPENDENCIES: Record<
  SolarUpdateKey,
  readonly SolarUpdateKey[]
> = {
  production: [],
  pricing: [`production`],
  savings: [`production`, `pricing`],
}

export const SOLAR_UPDATE_COPY: Record<SolarUpdateKey, UpdateCopy> = {
  production: {
    name: `Production`,
    stale: `Doesn't reflect the current design`,
    busy: `Retrying production`,
    action: `Retry`,
  },
  pricing: {
    name: `Pricing`,
    stale: `Out of date`,
    busy: `Refreshing pricing`,
    action: `Refresh`,
  },
  savings: {
    name: `Savings`,
    stale: `Out of date`,
    busy: `Recalculating savings`,
    action: `Recalculate`,
    plural: true,
  },
}

/** Each update's failure, in the copy its inline action used. */
export const describeSolarUpdateError = (
  key: SolarUpdateKey,
  error: unknown,
): string =>
  key === `production`
    ? parseAxiosError(error, PRODUCTION_RETRY_ERROR_PROMPT)
    : key === `pricing`
      ? solarPricingErrorMessage(error)
      : describeRecalculateError(error)

/**
 * Where a failed update is fixed instead, when the panel can't fix it: a
 * LightReach plan's Refresh Pricing waits on Get LightReach pricing and a
 * current offer, on the Loan Product step.
 */
export const solarUpdateFailureFix = (
  key: SolarUpdateKey,
  error: unknown,
  proposalId: string,
): UpdateFix | null =>
  key === `pricing` && isSolarLightReachPricingRequiredError(error)
    ? {
        path: solarSetupStepPath(proposalId, `loan-product`),
        label: OPEN_LIGHTREACH_LOAN_PRODUCT_LABEL,
      }
    : null

/**
 * A LightReach plan's price comes from choosing an offer, on Loan Product;
 * the panel links there instead of running Refresh Pricing.
 */
export const solarLightReachPricingFix = (proposalId: string): UpdateFix => ({
  path: solarSetupStepPath(proposalId, `loan-product`),
  label: GET_LIGHTREACH_PRICING_LABEL,
})

/** What stops an `Update all` that reaches a LightReach plan's pricing. */
export const SOLAR_LIGHTREACH_PRICING_UPDATE_MESSAGE =
  LIGHTREACH_PRICING_OUT_OF_DATE

/** The reads that say what's out of date; undefined while one is loading. */
export type SolarUpdateReads = {
  readonly design: SolarProposalDesignReadDto | undefined
  readonly pricing:
    | Pick<
        SolarProposalPricingDto,
        `financingBlocker` | `financingSummary` | `pricingStatus`
      >
    | undefined
  readonly savings:
    | Pick<SolarProposalSavingsSectionDto, `net` | `savingsStatus`>
    | undefined
  /**
   * The reads whose last check failed, by the update each answers for
   * (production is the design read's). Old data may still be on screen, but
   * whether it is current is unknown.
   */
  readonly unknown?: ReadonlySet<SolarUpdateKey>
  /** A LightReach plan: choosing an offer, not Refresh Pricing, sets the price. */
  readonly priceSetByOffer?: boolean
}

/**
 * Whether the panel can run an update itself. A LightReach plan's pricing is
 * Get LightReach pricing and an offer on Loan Product, which the row links to.
 */
export const isSolarUpdateRunnable = (
  key: SolarUpdateKey,
  { priceSetByOffer = false }: Pick<SolarUpdateReads, `priceSetByOffer`>,
): boolean => key !== `pricing` || !priceSetByOffer

/** Whether one update is needed, by its own read. A read still loading isn't. */
export const isSolarUpdateNeeded = (
  key: SolarUpdateKey,
  { design, pricing, savings, priceSetByOffer = false }: SolarUpdateReads,
): boolean => {
  switch (key) {
    case `production`:
      return design?.productionStatus === `not_updated`
    case `pricing`:
      // A LightReach plan without a current offer reads `loan_product`: Get
      // LightReach pricing on Loan Product clears it. Only another partner
      // clears `finance_partner`.
      if (priceSetByOffer) {
        return (
          pricing != null &&
          pricing.financingBlocker !== `finance_partner` &&
          (pricing.financingBlocker === `loan_product` ||
            pricing.pricingStatus !== `current` ||
            (pricing.financingSummary != null &&
              pricing.financingSummary.status !== `current`))
        )
      }
      // A Credit Human Quote that isn't current is Refresh Pricing's to fix,
      // unless the plan itself blocks pricing: Refresh would be refused, and
      // only Finance Partner or Loan Product can clear it.
      return (
        pricing != null &&
        pricing.financingBlocker == null &&
        (pricing.pricingStatus !== `current` ||
          (pricing.financingSummary != null &&
            pricing.financingSummary.status !== `current`))
      )
    case `savings`:
      // Net savings stale only through a Quote the plan blocks wait on Finance
      // Partner or Loan Product too: recalculating can't re-quote it.
      return (
        savings != null &&
        (savings.savingsStatus !== `current` ||
          (savings.net?.status === `out_of_date` &&
            pricing?.financingBlocker == null))
      )
  }
}

/**
 * The updates a proposal needs, in fix order. A proposal without an active
 * design has nothing to update yet: Setup starts there, and pricing and
 * savings can't run without one. A failed design read keeps pricing and
 * savings listed; the panel holds every fix that builds on a read whose
 * freshness is unknown.
 */
export const solarUpdatesNeeded = (
  reads: SolarUpdateReads,
): readonly SolarUpdateKey[] =>
  reads.design?.design != null || reads.unknown?.has(`production`) === true
    ? SOLAR_UPDATE_ORDER.filter((key) => isSolarUpdateNeeded(key, reads))
    : []

/** The figures each update leaves out of date, as the document marks them. */
export type SolarStaleFigures = {
  readonly savings: boolean
  readonly pricing: boolean
}

export const toSolarStaleFigures = (
  needed: readonly SolarUpdateKey[],
): SolarStaleFigures => ({
  savings: needed.includes(`savings`),
  pricing: needed.includes(`pricing`),
})

/** The section each update's row scrolls to, and the rail dots it badges. */
export const SOLAR_UPDATE_SECTIONS: Record<SolarUpdateKey, readonly string[]> = {
  production: [`home`, `system`],
  pricing: [`investment`, `savings`],
  savings: [`hero`, `savings`],
}

/** A row's state while it waits: never calculated, or out of date. */
export const solarUpdateStaleLabel = (
  key: SolarUpdateKey,
  { pricing, savings, priceSetByOffer = false }: SolarUpdateReads,
): string =>
  key === `pricing` && priceSetByOffer
    ? LIGHTREACH_UPDATE_STALE_LABEL
    : (key === `pricing` && pricing?.pricingStatus === `not_calculated`) ||
        (key === `savings` && savings?.savingsStatus === `not_calculated`)
      ? `Not calculated yet`
      : SOLAR_UPDATE_COPY[key].stale

/** The Solar Proposal's Updates panel: what it lists, in what order, and where. */
export const SOLAR_UPDATES_SPEC: UpdatesSpec<SolarUpdateKey> = {
  order: SOLAR_UPDATE_ORDER,
  dependencies: SOLAR_UPDATE_DEPENDENCIES,
  copy: SOLAR_UPDATE_COPY,
  sections: SOLAR_UPDATE_SECTIONS,
  idPrefix: `solar`,
}
